import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useAppDispatch } from '../hooks/redux';
import { fetchSpeechRateSettings } from '../store/conversationSlice';

interface SpeechRateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SpeechRateModal: React.FC<SpeechRateModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const { speechRateSettings, currentSpeechRate } = useSelector(
    (state: RootState) => state.conversation
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  const handleRefreshSettings = async () => {
    setIsRefreshing(true);
    setRefreshMessage(null);
    
    try {
      await dispatch(fetchSpeechRateSettings()).unwrap();
      setRefreshMessage('Settings refreshed successfully!');
      setTimeout(() => setRefreshMessage(null), 3000);
    } catch (error) {
      console.error('Failed to refresh settings:', error);
      setRefreshMessage('Failed to refresh settings');
      setTimeout(() => setRefreshMessage(null), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Speech Rate Monitor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Refresh Message */}
          {refreshMessage && (
            <div className={`px-3 py-2 rounded-lg text-sm font-medium text-center ${
              refreshMessage.includes('success') 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {refreshMessage}
            </div>
          )}

          {/* Settings Display (Read-only) */}
          <div className="bg-bone border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-obsidian">Current Settings</h3>
              <button
                onClick={handleRefreshSettings}
                disabled={isRefreshing}
                className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  isRefreshing 
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                    : 'bg-white hover:bg-bone text-obsidian border border-gray-300'
                }`}
                title="Refresh settings from server"
              >
                {isRefreshing ? (
                  <>
                    <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    <span>Refreshing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Refresh</span>
                  </>
                )}
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${speechRateSettings.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {speechRateSettings.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time Window:</span>
                <span className="font-medium text-gray-900">{speechRateSettings.time_window} seconds</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max Words per Window:</span>
                <span className="font-medium text-gray-900">{speechRateSettings.max_words} words</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3 italic">
              Settings can only be modified by administrators through the admin portal.
            </p>
          </div>

          {/* Current Speech Rate Display */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Current Speech Rate</h3>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-gray-900">
                {currentSpeechRate.words_per_minute} words/minute
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                currentSpeechRate.is_too_fast 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {currentSpeechRate.is_too_fast ? 'Fast' : 'Normal'}
              </span>
            </div>
          </div>

          {/* Info Message */}
          {currentSpeechRate.is_too_fast && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Slow down!</strong> You're speaking faster than the recommended pace. 
                Try to maintain under {speechRateSettings.max_words} words per {speechRateSettings.time_window} seconds.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-obsidian hover:opacity-90 text-bone rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpeechRateModal;